import { Resources } from 'constants/resources';
import { SECURITY_ISSUES_ROWS_BG } from 'constants/securityIssues';

import { useMemo } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { ApiClientsAccessField } from 'pages/ApiClients/components/ApiClientsAccessField/ApiClientsAccessField';
import { ApiClientsDeleteDialog } from 'pages/ApiClients/components/ApiClientsDeleteDialog/ApiClientsDeleteDialog';
import { ApiClientsLastActivityField } from 'pages/ApiClients/components/ApiClientsLastActivityField/ApiClientsLastActivityField';
import { AccessRole } from 'pages/ApiClients/constants/accessSelectItems';
import { FormProvider } from 'react-hook-form';
import { CreateButton } from 'shared/mui/Buttons';
import {
  ContextMenu,
  ContextMenuDeleteButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { ContextMenuEditButton } from 'shared/mui/ContextMenu/ContextMenuEditButton/ContextMenuEditButton';
import { FilterContextProvider } from 'shared/mui/Filter/context/filterContext';
import {
  FilterApplyButton,
  FilterButton,
  FilterClearButton,
  FilterResetButton,
} from 'shared/mui/Filter/FilterButtons';
import { FilterColumnsButton } from 'shared/mui/Filter/FilterButtons';
import { FilterChipsToolbar } from 'shared/mui/Filter/FilterChipsToolbar/FilterChipsToolbar';
import {
  FilterDrawer,
  FilterDrawerActionsButtons,
  FilterDrawerContent,
} from 'shared/mui/Filter/FilterDrawer';
import { FilterToolbar } from 'shared/mui/Filter/FilterToolbar/FilterToolbar';
import { useFilters } from 'shared/mui/Filter/hooks/useFilters';
import { ExportButton } from 'shared/react-admin/Buttons';
import { DateTimeField, TextField } from 'shared/react-admin/Fields';
import { SecurityIssuesField } from 'shared/react-admin/Fields/SecurityIssuesField/SecurityIssuesField';
import { TooltipTextField } from 'shared/react-admin/Fields/TooltipTextField/TooltipTextField';
import { TooltipTextLinkField } from 'shared/react-admin/Fields/TooltipTextLinkField/TooltipTextLinkField';
import { ListPage } from 'shared/react-admin/Pages';
import { ApiClient } from 'types/embedded/apiClient';
import { Permission } from 'types/embedded/permission';
import { SecurityIssuesTypes } from 'types/embedded/securityIssues';
import { cleanEmpty } from 'utils/cleanEmpty';

import { StyledDatagridConfigurableWithStickyActions } from './ApiClientsList.styled';
import { ApiClientsListActiveField } from './ApiClientsListActiveField/ApiClientsListActiveField';
import { ApiClientsListApiKeysField } from './ApiClientsListApiKeysField/ApiClientsListApiKeysField';
import { ApiClientsListFilter } from './ApiClientsListFilter/ApiClientsListFilter';
import { ApiClientsListFilterChips } from './ApiClientsListFilter/ApiClientsListFilterChips';
import { ApiClientsListMerchantField } from './ApiClientsListMerchantField/ApiClientsListMerchantField';

export type ApiClientsListFilterFormValues = {
  name: string | null;
  active: '1' | '0';
  roles: AccessRole.Merchant | AccessRole.Service | null;
  merchant: string | null;
  securityIssues: SecurityIssuesTypes | null;
};

const DEFAULT_VALUES: ApiClientsListFilterFormValues = {
  active: '1',
  name: null,
  roles: null,
  merchant: null,
  securityIssues: null,
};

export const ApiClientsList = () => {
  const { hasResourceAccess, hasResourcePermissions } = usePermissions();
  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<ApiClient>();

  const { form, appliedFilters, setAppliedFilter, onSubmit, onReset, openDrawer, toggleDrawer } =
    useFilters<ApiClientsListFilterFormValues>({
      mode: 'all',
      defaultValues: DEFAULT_VALUES,
      resetValues: DEFAULT_VALUES,
    });

  //TODO: useMemo избыточен и деструктуризация лишняя (во многих местах дублируется такая логика)
  const listFilters = useMemo(() => {
    return cleanEmpty({
      ...appliedFilters,
    });
  }, [appliedFilters]);

  const isListFilterApplied =
    Object.keys(listFilters).length > 0 && form.formState.isDirty && form.formState.isSubmitted;

  return (
    <ListPage
      actions={[
        hasResourcePermissions(Resources.ApiClients, [Permission.Full, Permission.Create]) && (
          <CreateButton key="create-button" />
        ),
        <ExportButton key="export-button" />,
      ]}
      empty={false}
      filter={listFilters}
      headerTitle={{
        titleText: 'Клиенты API',
        tooltipText:
          'Приложение, выполняющее запросы к API Kubera. Например, сайт мерчанта или его мобильное приложение.',
      }}
      listBoxProps={{ sx: { maxHeight: '100%' } }}
      sort={{ field: 'lastActivity', order: 'DESC' }}
    >
      <FilterToolbar
        leftActionsSlot={[
          <FilterButton key="filter" onClick={toggleDrawer(true)} />,
          <FilterColumnsButton key="select-column" />,
        ]}
      />
      <FilterDrawer onClose={toggleDrawer(false)} open={openDrawer}>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <FilterDrawerContent>
              <ApiClientsListFilter />
            </FilterDrawerContent>
            <FilterDrawerActionsButtons>
              <FilterResetButton label="Очистить" onReset={onReset} />
              <FilterApplyButton type="submit" />
            </FilterDrawerActionsButtons>
          </form>
        </FormProvider>
      </FilterDrawer>
      <FilterChipsToolbar appliedFilters={appliedFilters}>
        <FilterContextProvider appliedFilters={appliedFilters} setAppliedFilter={setAppliedFilter}>
          <FormProvider {...form}>
            <ApiClientsListFilterChips />
            <FilterClearButton
              key="clear-filter"
              onClick={() => {
                onReset();
              }}
              visible={isListFilterApplied}
            />
          </FormProvider>
        </FilterContextProvider>
      </FilterChipsToolbar>
      <StyledDatagridConfigurableWithStickyActions
        bulkActionButtons={false}
        rowSx={({ securityIssues }: ApiClient) => ({
          background: SECURITY_ISSUES_ROWS_BG[securityIssues[0]],
        })}
      >
        <TextField label="ID" sortBy="id" source="originId" />
        <ApiClientsListActiveField label="-" source="active" />
        <TooltipTextLinkField
          label="Название"
          resource={Resources.ApiClients}
          source="name"
          type="show"
        />
        <TooltipTextField label="Алиас" source="alias" />
        <ApiClientsAccessField label="Доступ" />
        <ApiClientsLastActivityField
          label="Последняя активность"
          sortBy="lastActivity"
          source="activityUpdatedAt"
        />
        {hasResourceAccess(Resources.Merchants) && <ApiClientsListMerchantField label="Мерчанты" />}
        {hasResourceAccess('api_keys') && <ApiClientsListApiKeysField label="API ключи" />}
        <SecurityIssuesField label="Уведомления безопасности" source="securityIssues" />
        <DateTimeField label="Дата создания" source="createdAt" />
        <DateTimeField label="Дата редактир." source="updatedAt" />
        <ContextMenu
          render={(record: ApiClient) => [
            hasResourceAccess(Resources.UserActions) && (
              <ContextMenuEntityUserActionsButton
                entityId={record?.originId}
                entityName="api_client"
                key="user-action-link"
              />
            ),
            <ContextMenuShowButton
              key="show-button"
              linkProps={{ resource: Resources.ApiClients, target: '_blank' }}
            />,
            hasResourcePermissions(Resources.ApiClients, [Permission.Full, Permission.Update]) && (
              <ContextMenuEditButton
                key="edit-button"
                linkProps={{ resource: Resources.ApiClients, target: '_blank' }}
              />
            ),
            hasResourcePermissions(Resources.ApiClients, [Permission.Full, Permission.Delete]) && (
              <ContextMenuDeleteButton
                key="delete-button"
                onClick={() => handleOpenRecordModalDelete(record)}
              />
            ),
          ]}
        />
      </StyledDatagridConfigurableWithStickyActions>
      {openRecordModalDelete && (
        <ApiClientsDeleteDialog
          onClose={handleCloseRecordModalDelete}
          onSuccess={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          record={recordDelete}
        />
      )}
    </ListPage>
  );
};
